/**
 * ═══════════════════════════════════════════════════════════════════════
 * SyncDebugger — Runtime Behaviour Audit Instrumentation
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Detects at runtime:
 *   • Same API called >1 time within 3 seconds
 *   • Duplicate WS subscriptions (EventBus listener stacking)
 *   • Polling overlap with WS updates
 *   • Polling running while WS is healthy
 *   • useEffect re-runs more than expected
 *   • Metadata API called without visitId change
 *
 * Enable via: REACT_APP_DEBUG=true
 * Access diagnostics via: window.__akSyncDebug()
 *
 * This module is ZERO-COST in production (all instrumentation
 * is behind _debug flag).
 * ═══════════════════════════════════════════════════════════════════════
 */

import eventBus from './realtime/EventBus';
import realtimeSyncService from './realtime/RealtimeSyncService';

const _debug = process.env.REACT_APP_DEBUG === 'true';

// ─── Effect re-run tracking ──────────────────────────────────────────
// Maps effectKey → { count, lastFiredAt, args }
const _effectTracker = new Map();
const EFFECT_WARN_THRESHOLD = 5;  // Warn if effect fires >5x in 10s
const EFFECT_WINDOW_MS = 10000;

// ─── WS subscription tracking ───────────────────────────────────────
// Maps eventType → count of active listeners
const _wsSubscriptionCounts = new Map();

// ─── Polling/WS overlap detection ────────────────────────────────────
const _lastPollTimestamp = { value: 0 };
const _lastWsEventTimestamp = { value: 0 };
const OVERLAP_WINDOW_MS = 2000; // If poll + WS event within 2s = overlap

// ─── Metadata call tracking ─────────────────────────────────────────
const _metadataCallLog = new Map(); // visitId → lastCalledAt

/**
 * Track a useEffect execution for loop detection.
 *
 * Usage in component:
 *   useEffect(() => {
 *     trackEffect('MyComponent:loadData', [dep1, dep2]);
 *     // ... effect body
 *   }, [dep1, dep2]);
 *
 * @param {string} effectKey  - Unique identifier for this effect
 * @param {Array}  deps       - Current dependency values (for change detection)
 */
export function trackEffect(effectKey, deps = []) {
  if (!_debug) return;

  const now = Date.now();
  const entry = _effectTracker.get(effectKey) || { timestamps: [], lastDeps: null };

  // Trim old timestamps
  entry.timestamps = entry.timestamps.filter(
    (t) => now - t < EFFECT_WINDOW_MS
  );
  entry.timestamps.push(now);

  // Check if deps actually changed
  const depsChanged = !entry.lastDeps ||
    deps.length !== entry.lastDeps.length ||
    deps.some((d, i) => d !== entry.lastDeps[i]);

  if (!depsChanged && entry.timestamps.length > 1) {
    console.warn(
      `[SyncDebug] ⚠️ Effect "${effectKey}" re-fired WITHOUT dependency change`,
      `\n  Fired ${entry.timestamps.length}x in ${EFFECT_WINDOW_MS / 1000}s`,
      `\n  This indicates unstable callback/object identity in deps.`
    );
  }

  if (entry.timestamps.length > EFFECT_WARN_THRESHOLD) {
    console.warn(
      `[SyncDebug] 🔴 Effect "${effectKey}" fired ${entry.timestamps.length}x in ${EFFECT_WINDOW_MS / 1000}s`,
      `\n  Likely cause: re-render loop, unstable dependency, or missing memo.`,
      `\n  Deps changed: ${depsChanged}`
    );
  }

  entry.lastDeps = [...deps];
  _effectTracker.set(effectKey, entry);
}

/**
 * Track a metadata API call for loop detection.
 *
 * @param {string} visitId
 * @param {string} source - Component/function name
 */
export function trackMetadataCall(visitId, source) {
  if (!_debug) return;

  const now = Date.now();
  const key = `metadata:${visitId}`;
  const lastCall = _metadataCallLog.get(key);

  if (lastCall && now - lastCall.timestamp < 3000) {
    console.warn(
      `[SyncDebug] ⚠️ Metadata API for visit "${visitId}" called again within 3s`,
      `\n  Previous: ${lastCall.source} at ${new Date(lastCall.timestamp).toISOString()}`,
      `\n  Current: ${source}`,
      `\n  Use SyncEngine.fetchMetadata() for deduplication.`
    );
  }

  _metadataCallLog.set(key, { timestamp: now, source });
}

/**
 * Track WS subscription for stacking detection.
 *
 * @param {string} eventType
 * @param {string} componentName
 * @returns {Function} untrack function
 */
export function trackWsSubscription(eventType, componentName) {
  if (!_debug) return () => {};

  const current = _wsSubscriptionCounts.get(eventType) || { count: 0, sources: [] };
  current.count++;
  current.sources.push(componentName);

  if (current.count > 3) {
    console.warn(
      `[SyncDebug] ⚠️ ${current.count} active subscriptions for "${eventType}"`,
      `\n  Sources: ${current.sources.join(', ')}`,
      `\n  Likely cause: missing cleanup in useEffect or listener stacking.`
    );
  }

  _wsSubscriptionCounts.set(eventType, current);

  return () => {
    const entry = _wsSubscriptionCounts.get(eventType);
    if (entry) {
      entry.count--;
      entry.sources = entry.sources.filter((s) => s !== componentName);
      if (entry.count <= 0) _wsSubscriptionCounts.delete(eventType);
    }
  };
}

/**
 * Detect polling/WS overlap.
 * Call this when a poll completes.
 */
export function trackPollComplete() {
  if (!_debug) return;

  const now = Date.now();
  _lastPollTimestamp.value = now;

  if (realtimeSyncService.isConnected && realtimeSyncService.mode === 'ws') {
    console.warn(
      `[SyncDebug] ⚠️ Poll completed while WS is connected and healthy`,
      `\n  WS mode: ${realtimeSyncService.mode}`,
      `\n  This may be the 60s reconciliation (expected) or unnecessary polling.`
    );
  }

  if (now - _lastWsEventTimestamp.value < OVERLAP_WINDOW_MS) {
    console.warn(
      `[SyncDebug] ⚠️ Poll and WS update overlapped within ${OVERLAP_WINDOW_MS}ms`,
      `\n  Both triggered data fetches — potential for duplicate state updates.`
    );
  }
}

/**
 * Track WS event for overlap detection.
 */
export function trackWsEvent(eventType) {
  if (!_debug) return;
  _lastWsEventTimestamp.value = Date.now();
}

/**
 * Install global debug instrumentation.
 * Call once at app startup.
 */
export function installSyncDebugger() {
  if (!_debug) return;

  // Idempotent — prevent duplicate intervals + listeners on HMR re-runs
  if (installSyncDebugger._installed) return;
  installSyncDebugger._installed = true;

  console.log('[SyncDebug] 🔍 Debug instrumentation active');

  // Monitor EventBus listener counts periodically.
  //
  // A *high* count is not itself a leak — the app has a large static
  // baseline (4 realtimeMiddleware stores each subscribe one listener per
  // handled event type + a snapshot listener, plus dentalHistoryStore's
  // ~17 invalidation handlers, appointmentStore, staffApi, and the
  // app-shell useRealtimeSync hooks ≈ 90 listeners). EventBus also caps
  // each event type at MAX_LISTENERS_PER_TYPE and logs its own warning if a
  // single type runs away, which is the true subscribe-without-cleanup
  // signature. So warn only on *sustained growth* — a count that keeps
  // climbing across consecutive checks — not on a high-but-stable baseline.
  let prevCount = eventBus.listenerCount();
  let consecutiveGrowth = 0;
  const checkInterval = setInterval(() => {
    const total = eventBus.listenerCount();
    consecutiveGrowth = total > prevCount ? consecutiveGrowth + 1 : 0;
    prevCount = total;
    if (consecutiveGrowth >= 3) {
      console.warn(
        `[SyncDebug] 🔴 EventBus listeners climbing for ${consecutiveGrowth} consecutive checks (now ${total})`,
        `\n  Possible listener leak — the count keeps rising. Check for missing cleanup in useEffect.`
      );
    }
  }, 30000);

  // Monitor reconnect → listener stacking
  eventBus.on('REALTIME_CONNECTED', () => {
    const listeners = eventBus.listenerCount();
    console.log(`[SyncDebug] ↻ Reconnected. EventBus listeners: ${listeners}`);
  });

  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    clearInterval(checkInterval);
  });
}

/**
 * Get all debug diagnostics.
 */
export function getSyncDebugDiag() {
  const effects = {};
  _effectTracker.forEach((entry, key) => {
    effects[key] = {
      recentFireCount: entry.timestamps.length,
      lastFired: entry.timestamps.length > 0
        ? `${Math.round((Date.now() - entry.timestamps[entry.timestamps.length - 1]) / 1000)}s ago`
        : 'never',
    };
  });

  const subscriptions = {};
  _wsSubscriptionCounts.forEach((entry, type) => {
    subscriptions[type] = {
      activeCount: entry.count,
      sources: entry.sources,
    };
  });

  return {
    effects,
    wsSubscriptions: subscriptions,
    metadataCallLog: Object.fromEntries(_metadataCallLog),
    eventBusListenerCount: eventBus.listenerCount(),
    wsConnected: realtimeSyncService.isConnected,
    wsMode: realtimeSyncService.mode,
  };
}

// ── Global diagnostic ────────────────────────────────────────────────
if (typeof window !== 'undefined' && _debug) {
  window.__akSyncDebug = getSyncDebugDiag;
}

const SyncDebugger = {
  trackEffect,
  trackMetadataCall,
  trackWsSubscription,
  trackPollComplete,
  trackWsEvent,
  installSyncDebugger,
  getSyncDebugDiag,
};

export default SyncDebugger;
